/**
 * Variables CSS --accent / --accent-light / --accent-text d'une page publique.
 *
 * Une SEULE règle pour la billetterie, les adhésions et la boutique : la même
 * couleur doit donner le même rendu partout, sinon les trois pages divergent
 * (c'était le cas avant le 2026-09-12 — les adhésions n'avaient RIEN, boutons
 * noirs en dur).
 *
 * La couleur vient de `accent_color`, pré-remplie par extraction de l'image de
 * couverture à l'upload et modifiable dans la configuration de l'élément.
 *
 * Le paramètre est typé sur la forme MINIMALE plutôt que sur `Event` : c'est ce
 * qui permet aux trois domaines de l'utiliser sans dépendre du type de l'autre.
 */

/** Toute entité publique portant une couleur d'accent. */
interface PorteurAccent {
  accent_color?: string | null;
}

function hexToLuminance(hex: string): number {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function hexToLight(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lr = Math.round(r + (255 - r) * 0.9);
  const lg = Math.round(g + (255 - g) * 0.9);
  const lb = Math.round(b + (255 - b) * 0.9);
  return "#" + [lr, lg, lb].map(c => c.toString(16).padStart(2, "0")).join("");
}

export function useEventAccentColor(
  // `ComputedRef` accepté en plus de `Ref` : les pages publiques dérivent
  // souvent leur entité d'un `computed` (lecture seule), et le refuser
  // obligerait chaque appelant à un cast.
  event: Ref<PorteurAccent | null> | ComputedRef<PorteurAccent | null>,
) {
  const accentColor = computed(() => event.value?.accent_color || null);

  const accentStyle = computed(() => {
    if (!accentColor.value) return {};
    return {
      "--accent": accentColor.value,
      "--accent-light": hexToLight(accentColor.value),
      "--accent-text": hexToLuminance(accentColor.value) < 0.6 ? "#ffffff" : "#1a1a1a",
    } as Record<string, string>;
  });

  return { accentColor, accentStyle };
}

/**
 * Pose la couleur des boutons sur <html> (dès le rendu serveur) : pour les
 * pages dont le header est monté HORS de la page (page d'une association,
 * header de app.vue). Sans couleur : rien n'est posé, bleu Donoor.
 * Karim 2026-10-21.
 */
export function useCouleurBoutonsSurLaPage(
  source: Ref<PorteurAccent | null> | ComputedRef<PorteurAccent | null>,
) {
  const { accentStyle } = useEventAccentColor(source);
  useHead({
    htmlAttrs: {
      style: computed(() =>
        Object.entries(accentStyle.value)
          .map(([cle, valeur]) => `${cle}:${valeur}`)
          .join(";"),
      ),
    },
  });
}
